import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useEffect, useRef, useState } from 'react'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'

import { useAppDispatch } from 'redux/Hook'
import { setPage, setUserFilters } from 'redux/slices/TextAnalysis'
import {
  FilterAttributes,
  MultiSelectValue,
  shouldShowTooltip,
  truncatedLabel,
} from '../Helper'
import FilterOption from './FilterOption'
import { useFilterData } from './Hook'

const MultiSelectFilter = (props: FilterAttributes) => {
  const inputRef = useRef<HTMLInputElement>()
  const dispatch = useAppDispatch()
  const { attribute, label, apiSource, selectType, options } = props
  const [value, setValue] = useState<
    MultiSelectValue | MultiSelectValue[] | undefined
  >([])

  const autoCompleteOptions = options?.length
    ? options
    : useFilterData({ apiSource })

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <Autocomplete
      limitTags={1}
      size="small"
      disableCloseOnSelect
      value={value}
      disablePortal
      openOnFocus
      loading
      options={autoCompleteOptions}
      sx={{ width: { md: 340 } }}
      getOptionLabel={(option: MultiSelectValue) => option?.label || ''}
      renderOption={(props, option, { selected }) => (
        <FilterOption {...props} {...option} selected={selected} />
      )}
      multiple={selectType === 'multiple'}
      renderTags={(value, getTagProps) =>
        value.map(({ label }, index) => {
          const { key, ...tagProps } = getTagProps({ index })
          return (
            <Tooltip
              key={key}
              sx={{ zIndex: { md: 9999 } }}
              title={shouldShowTooltip(label) ? label : ''}
            >
              <Chip
                size="small"
                color="primary"
                label={truncatedLabel(label)}
                {...tagProps}
              />
            </Tooltip>
          )
        })
      }
      onChange={(_, newValue) => {
        dispatch(setPage(1))
        setValue(newValue as MultiSelectValue[])
        dispatch(
          setUserFilters({
            [attribute]: Array.isArray(newValue)
              ? newValue.map(({ id }) => id)
              : newValue?.id,
          }),
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder="Search"
          inputRef={inputRef}
          sx={{
            position: { md: 'absolute' },
            width: { md: 340 },
            bgcolor: 'grey.100',
            zIndex: { md: 1000 },
          }}
        />
      )}
    />
  )
}

export default MultiSelectFilter
